/**
 * Извлечение машиночитаемой причины ошибки OAuth из ответа бэкенда.
 *
 * Контракт (см. backend/utils/errors/errors.go → AppError.Respond):
 *   {
 *     "error": {
 *       "code": "EXTERNAL_SERVICE_ERROR",
 *       "message": "...",              // технический текст, не для показа
 *       "metadata": { "oauth_error": "token_exchange_failed" }
 *     }
 *   }
 *
 * Раньше callback-страницы делали `new Error(errorData.error || "OAuth failed")`,
 * но `errorData.error` — объект, а не строка, поэтому пользователь видел
 * "Error: [object Object]". Здесь разбираем вложенную структуру и отдаём
 * i18n-ключ по коду причины.
 */

/** Коды причин, которые backend кладёт в error.metadata.oauth_error. */
export const OAUTH_ERROR_CODES = [
  "token_exchange_failed",
  "user_info_failed",
  "user_creation_failed",
  "access_denied",
  "invalid_state",
  "missing_code",
  "jwt_generation_failed",
] as const;

export type OAuthErrorCode = (typeof OAUTH_ERROR_CODES)[number];

/** i18n-ключ для кода причины; неизвестные коды получают общий ключ. */
export function oauthErrorKey(code: string | null | undefined): string {
  const match = OAUTH_ERROR_CODES.find((c) => c === code);
  return match ? `oauth_error_${match}` : "oauth_error_generic";
}

/** Достаёт код причины из тела ответа бэкенда (любой формы вложенности). */
export function extractOAuthErrorCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err && typeof err === "object") {
    const metadata = (err as { metadata?: unknown }).metadata;
    if (metadata && typeof metadata === "object") {
      const code = (metadata as { oauth_error?: unknown }).oauth_error;
      if (typeof code === "string" && code) return code;
    }
  }
  return null;
}
